/**
 * inspection 共有 URL のアクセス状態を保持する Store
 * session_key と同様に localStorage へ保存し、リロード後も有効期限内で再利用できるようにする
 */

import { Injectable } from '@angular/core';
import { STORAGE_KEYS } from '../constants/app.constants';
import { SessionStorageService } from '../services/session-storage.service';
import { normalizeUtcDateTimeString, parseUtcDateTime } from './session-time.util';

export interface InspectionAccessContext {
  sessionKey: string;
  tabId: string;
  viewCd: number;
  accessToken: string;
  expiresAt: string;
}

@Injectable({ providedIn: 'root' })
export class InspectionAccessStore {
  constructor(private readonly storage: SessionStorageService) {}

  get snapshot(): InspectionAccessContext | null {
    return this.loadFromStorage();
  }

  upsert(context: InspectionAccessContext): void {
    const normalized = this.normalizeContext(context);
    if (!normalized) {
      this.clear();
      return;
    }

    this.storage.setItem(STORAGE_KEYS.INSPECTION_ACCESS_CONTEXT, normalized);
  }

  clear(): void {
    this.storage.removeItem(STORAGE_KEYS.INSPECTION_ACCESS_CONTEXT);
  }

  isExpired(context: InspectionAccessContext | null = this.snapshot): boolean {
    if (!context) {
      return true;
    }

    const expiresAtTime = parseUtcDateTime(context.expiresAt);
    if (Number.isNaN(expiresAtTime)) {
      return true;
    }

    return expiresAtTime <= Date.now();
  }

  private loadFromStorage(): InspectionAccessContext | null {
    const stored = this.storage.getItem<InspectionAccessContext>(STORAGE_KEYS.INSPECTION_ACCESS_CONTEXT);
    return this.normalizeContext(stored);
  }

  private normalizeContext(context: InspectionAccessContext | null): InspectionAccessContext | null {
    if (!context || !context.sessionKey || !context.tabId || !context.accessToken || !Number.isInteger(context.viewCd)) {
      return null;
    }

    const normalizedExpiresAt = normalizeUtcDateTimeString(context.expiresAt);
    if (!normalizedExpiresAt) {
      return null;
    }

    return {
      ...context,
      expiresAt: normalizedExpiresAt,
    };
  }
}
